import { Link } from "react-router-dom";
import { ExternalLink, Siren, Scale, Send } from "lucide-react";
import { theme, card, btnPrimary, pageContainer, pageHeading, pageSubtitle } from "@main/theme";

// ── Jersey Cyber Security Centre ──────────────────────────────────────────
// The one authority the Jersey edition reports to. Incident notifications are
// derived on the server (backend/src/lib/resilienceRules.ts, narrowed to the JCSC
// by lib/editions.ts): the Director's 24-hour notification under the Cyber
// Security (Jersey) Law 2026 for an operator of essential services, and JCSC's
// voluntary incident sharing for everyone else. This page explains that and
// links to the registers where it happens; it states no clock of its own.

const JCSC_URL = "https://www.jcsc.je";

export function JcscPage() {
  return (
    <div style={pageContainer}>
      <h1 style={pageHeading}>Jersey Cyber Security Centre</h1>
      <p style={pageSubtitle}>Who you report to, and when.</p>

      <div style={{ display: "grid", gap: 16 }}>
        <div style={card}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
            <Siren size={18} color="var(--c-danger)" />
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>Something has happened</div>
          </div>
          <p style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.7, margin: "0 0 14px" }}>
            Record the incident as soon as you are aware of it. The platform works out who must be told and by when from
            your organisation's position under the Cyber Security (Jersey) Law 2026, and starts the clock from the moment
            you became aware.
          </p>
          <Link to="/resilience/incidents" style={{ ...btnPrimary, display: "inline-block", textDecoration: "none" }}>Report an incident</Link>
        </div>

        <div style={card}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
            <Scale size={18} color="var(--c-accent)" />
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>Operators of essential services</div>
          </div>
          <p style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.7, margin: 0 }}>
            An organisation designated as an operator of essential services must notify the Director of the JCSC of a
            significant incident within the statutory window, and keep its registration and returns up to date. Whether
            you could be designated depends on your sector and the thresholds for it — answer the questions under
            Jersey cyber law → <Link to="/legal" style={{ color: theme.color.accent }}>Your obligations</Link> and the
            platform will tell you, and prepare your returns under Jersey cyber law →{" "}
            <Link to="/submissions" style={{ color: theme.color.accent }}>JCSC returns</Link>.
          </p>
        </div>

        <div style={card}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
            <Send size={18} color="var(--c-accent)" />
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>Everyone else</div>
          </div>
          <p style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.7, margin: "0 0 12px" }}>
            If you are not an operator of essential services, reporting to the JCSC is voluntary — and still worth doing:
            it helps the JCSC warn other island organisations about the same attack. The platform lists the JCSC as a
            voluntary notification on every incident you record.
          </p>
          <a href={JCSC_URL} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: theme.color.accent, display: "inline-flex", alignItems: "center", gap: 6 }}>
            Visit the JCSC website <ExternalLink size={13} />
          </a>
        </div>
      </div>
    </div>
  );
}
